import type { CopilotHeadlinesOptionStore } from '../store/store';
import type { MailboxInfo } from 'owa-client-types';
import { getIndexerValueForMailboxInfo } from 'owa-client-types';
import { getItem, setItem, itemExists, type LocalStorageKeys } from 'owa-local-storage';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { logUsage } from 'owa-analytics';

/**
 * Get the copilot headlines settings for the mailbox
 * @param mailboxInfo the mailbox info for current user
 */
export function getCopilotHeadlinesOptionStore(
    mailboxInfo: MailboxInfo
): CopilotHeadlinesOptionStore {
    const key = getIndexerValueForMailboxInfo(mailboxInfo);
    const localStorageData = fetchCopilotHeadlinesFromLocalStorage(key);

    // Handle mutual exclusivity: Headlines has priority
    const isHeadlinesEnabled =
        isGlobalFeatureEnabled('mon-copilot-headlines') &&
        (localStorageData?.isHeadlinesEnabled ?? true);
    const isCatchUpEnabled =
        !isHeadlinesEnabled &&
        isGlobalFeatureEnabled('mon-copilot-catchup') &&
        (localStorageData?.isCatchUpEnabled ?? false);

    return {
        id: localStorageData?.id ?? key,
        // Mutual exclusivity flags
        isHeadlinesEnabled,
        isCatchUpEnabled,
        // Headlines properties
        headlinesIncludeAllEmail: localStorageData?.headlinesIncludeAllEmail ?? true,
        autoGenerateOnSessionStart:
            (isGlobalFeatureEnabled('mon-copilot-headlines-auto') &&
                localStorageData?.autoGenerateOnSessionStart) ??
            false,
        selectedHeadlineItemIds: [],
        selectedHeadlineConversationIds: [],
        // CatchUp properties
        catchUpIncludeAllEmail: localStorageData?.catchUpIncludeAllEmail ?? true,
        shouldUsePrompt: localStorageData?.shouldUsePrompt ?? true,
        shouldApplyPromptToIncomingMessages:
            localStorageData?.shouldApplyPromptToIncomingMessages ?? false,
        timeFrame: localStorageData?.timeFrame ?? '3days',
        needsReplyItemIds: localStorageData?.needsReplyItemIds ?? [],
        needsReplyConversationIds: localStorageData?.needsReplyConversationIds ?? [],
        needsActionItemIds: localStorageData?.needsActionItemIds ?? [],
        needsActionConversationIds: localStorageData?.needsActionConversationIds ?? [],
        needsScheduleItemIds: localStorageData?.needsScheduleItemIds ?? [],
        needsScheduleConversationIds: localStorageData?.needsScheduleConversationIds ?? [],
        extractedTaskDetails: localStorageData?.extractedTaskDetails ?? {},
    };
}

/**
 * Save the copilot headlines settings to local storage
 * @param data the copilot headlines settings to save
 * @param mailboxInfo the mailbox info for current user
 */
export function saveCopilotHeadlinesOptionStore(
    data: CopilotHeadlinesOptionStore,
    mailboxInfo: MailboxInfo
): void {
    const key = getIndexerValueForMailboxInfo(mailboxInfo);
    const localStorageKey: LocalStorageKeys = `mail_copilot_headlines_${key}`;

    try {
        setItem(window, localStorageKey, JSON.stringify(data));

        logUsage('CopilotHeadlinesSettingsSaved', mailboxInfo, {
            isHeadlinesEnabled: data.isHeadlinesEnabled,
            isCatchUpEnabled: data.isCatchUpEnabled,
            headlinesIncludeAllEmail: data.headlinesIncludeAllEmail,
            catchUpIncludeAllEmail: data.catchUpIncludeAllEmail,
            autoGenerateOnSessionStart: data.autoGenerateOnSessionStart,
            shouldUsePrompt: data.shouldUsePrompt,
            shouldApplyPromptToIncomingMessages: data.shouldApplyPromptToIncomingMessages,
            timeFrame: data.timeFrame,
        });
    } catch {
        // Silently fail if unable to save to local storage
    }
}

/**
 * Get the copilot settings from local storage
 * @param key the key to fetch the settings
 */
function fetchCopilotHeadlinesFromLocalStorage(key: string): any | undefined {
    const localStorageKey: LocalStorageKeys = `mail_copilot_headlines_${key}`;
    try {
        return itemExists(window, localStorageKey)
            ? JSON.parse(getItem(window, localStorageKey) as string)
            : undefined;
    } catch {
        return undefined;
    }
}
